/**
 * ResponsePhaseController - 回應階段控制器
 * 管理回應階段的流程，包含計時、輪轉優先權、結算
 */

import type { GameState, CardDefinition } from '../../types';
import type {
  ChainEntry,
  ResponseOption,
  ResponseTriggerType,
  ResponseResult,
} from '../../types/response';
import { ResponseChainManager } from '../effects/ResponseChainManager';
import type { ImmediateAction } from '../effects/EffectTriggerSystem';
import { getLogger } from '../../utils/logger';

const log = getLogger('ResponsePhaseController');

// ============================================================
// 類型定義
// ============================================================

export interface ResponsePhaseConfig {
  /** 預設回應時間（毫秒） */
  defaultTimeout: number;
  /** 連續 pass 後的縮短時間 */
  reducedTimeout: number;
  /** 連續 pass 幾次後縮短時間 */
  passThreshold: number;
  /** AI 最小假延遲（毫秒） */
  aiMinDelay: number;
  /** AI 最大假延遲（毫秒） */
  aiMaxDelay: number;
  /** 是否啟用計時器（單機可關閉） */
  enableTimer: boolean;
}

export interface ResponsePhaseState {
  /** 是否在回應階段 */
  isActive: boolean;
  /** 觸發回應的事件類型 */
  triggerType: ResponseTriggerType | null;
  /** 觸發回應的卡牌 ID */
  triggerCardId: string | null;
  /** 觸發回應的卡牌名稱 */
  triggerCardName: string | null;
  /** 觸發者玩家 ID */
  triggerPlayerId: string | null;
  /** 當前擁有優先權的玩家 ID */
  currentPriority: string | null;
  /** 可用的回應選項 */
  availableResponses: ResponseOption[];
  /** 剩餘時間（毫秒） */
  timeRemaining: number | null;
  /** 開始時間 */
  startTime: number | null;
  /** 連續 pass 次數 */
  consecutivePasses: number;
  /** 是否等待 AI 回應 */
  waitingForAI: boolean;
}

export type ResponsePhaseEventType =
  | 'phase_start'
  | 'phase_end'
  | 'priority_change'
  | 'response_received'
  | 'pass_received'
  | 'timeout'
  | 'chain_resolve_start'
  | 'chain_resolve_end';

export interface ResponsePhaseEvent {
  type: ResponsePhaseEventType;
  playerId?: string;
  response?: ResponseOption;
  chainEntry?: ChainEntry;
}

export type ResponsePhaseListener = (event: ResponsePhaseEvent) => void;

// ============================================================
// 預設配置
// ============================================================

const DEFAULT_CONFIG: ResponsePhaseConfig = {
  defaultTimeout: 5000,
  reducedTimeout: 2000,
  passThreshold: 3,
  aiMinDelay: 800,
  aiMaxDelay: 2500,
  enableTimer: false, // 單機版預設關閉計時
};

// ============================================================
// 工具函數
// ============================================================

function randomBetween(min: number, max: number): number {
  return Math.floor(Math.random() * (max - min + 1)) + min;
}

function delay(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms));
}

// ============================================================
// ResponsePhaseController
// ============================================================

export class ResponsePhaseController {
  private chainManager: ResponseChainManager;
  private config: ResponsePhaseConfig;
  private state: ResponsePhaseState;
  private listeners: Set<ResponsePhaseListener> = new Set();
  private timerHandle: ReturnType<typeof setTimeout> | null = null;
  private resolveWait: ((result: ResponseResult) => void) | null = null;
  private aiPlayerId: string | null = null;

  constructor(
    cardDefinitions?: Map<string, CardDefinition>,
    config?: Partial<ResponsePhaseConfig>
  ) {
    this.chainManager = new ResponseChainManager(cardDefinitions);
    this.config = { ...DEFAULT_CONFIG, ...config };
    this.state = this.createInitialState();
  }

  // ==================== 公開 API ====================

  /**
   * 設定 AI 玩家 ID
   */
  setAIPlayer(playerId: string): void {
    this.aiPlayerId = playerId;
  }

  /**
   * 設定卡牌定義
   */
  setCardDefinitions(definitions: Map<string, CardDefinition>): void {
    this.chainManager.setCardDefinitions(definitions);
  }

  /**
   * 取得當前狀態
   */
  getState(): ResponsePhaseState {
    return { ...this.state };
  }

  /**
   * 註冊事件監聽器
   */
  on(listener: ResponsePhaseListener): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  /**
   * 開始回應階段
   * 當玩家打出卡牌或觸發效果時呼叫
   */
  async startResponsePhase(
    gameState: GameState,
    triggerType: ResponseTriggerType,
    triggerPlayerId: string,
    triggerCardId: string,
    triggerCardName: string,
    effectText: string,
    actions: ImmediateAction[],
    targets?: string[]
  ): Promise<ResponseResult> {
    const opponentId = this.getOpponentId(gameState, triggerPlayerId);

    // 初始化連鎖
    this.chainManager.startChain(
      triggerCardId,
      triggerCardName,
      triggerPlayerId,
      effectText,
      actions,
      opponentId,
      targets
    );

    // 取得對手可用的回應
    const availableResponses = this.chainManager.getAvailableResponses(
      gameState,
      opponentId,
      triggerType
    );

    // 更新狀態
    this.state = {
      isActive: true,
      triggerType,
      triggerCardId,
      triggerCardName,
      triggerPlayerId,
      currentPriority: opponentId,
      availableResponses,
      timeRemaining: this.getCurrentTimeout(),
      startTime: Date.now(),
      consecutivePasses: 0,
      waitingForAI: opponentId === this.aiPlayerId,
    };

    this.emit({ type: 'phase_start' });

    // 如果對手沒有可回應的牌，直接 pass（但要有延遲模擬思考）
    if (availableResponses.length === 0) {
      if (opponentId === this.aiPlayerId) {
        // AI 假裝思考
        await delay(randomBetween(this.config.aiMinDelay, this.config.aiMaxDelay));
      }
      
      // AI pass 後，切換優先權給觸發者
      this.handlePass(opponentId);
      
      // 切換優先權給觸發者（施法者）
      await this.switchPriority(gameState, triggerPlayerId);
      
      // 等待觸發者決定是否回應自己的效果
      return new Promise(resolve => {
        this.resolveWait = resolve;
      });
    }

    // 啟動計時器（如果啟用）
    if (this.config.enableTimer) {
      this.startTimer();
    }

    // 等待回應
    return new Promise(resolve => {
      this.resolveWait = resolve;

      // 如果是 AI 回合，自動處理
      if (opponentId === this.aiPlayerId) {
        void this.handleAIResponse(gameState, availableResponses);
      }
    });
  }

  /**
   * 玩家選擇回應
   */
  async respond(
    gameState: GameState,
    playerId: string,
    response: ResponseOption,
    targets?: string[]
  ): Promise<ResponseResult> {
    // 驗證優先權
    if (playerId !== this.state.currentPriority) {
      log.warn(`${playerId} 沒有優先權`);
      return { responded: false };
    }

    // 停止計時
    this.stopTimer();

    // 加入連鎖
    const chainEntry = this.chainManager.respond(
      response.cardId,
      response.cardName,
      playerId,
      response.effectText,
      [], // actions 需要從效果系統取得
      targets
    );

    if (!chainEntry) {
      return { responded: false };
    }

    this.emit({ type: 'response_received', playerId, response, chainEntry });

    // 重置連續 pass
    this.state.consecutivePasses = 0;

    // 優先權交給對方
    const opponentId = this.getOpponentId(gameState, playerId);
    await this.switchPriority(gameState, opponentId);

    return {
      responded: true,
      responseOption: response,
      selectedTargets: targets,
      chainEntry,
    };
  }

  /**
   * 玩家放棄回應
   */
  pass(playerId: string): ResponseResult {
    // 驗證優先權
    if (playerId !== this.state.currentPriority) {
      log.warn(`${playerId} 沒有優先權`);
      return { responded: false };
    }

    return this.handlePass(playerId);
  }

  /**
   * 結算連鎖
   */
  resolveChain(
    gameState: GameState,
    executeAction: (state: GameState, action: ImmediateAction) => GameState
  ): GameState {
    this.emit({ type: 'chain_resolve_start' });

    const result = this.chainManager.resolveChain(gameState, executeAction);

    this.emit({ type: 'chain_resolve_end' });
    this.endPhase();

    return result.finalState as GameState;
  }

  /**
   * 強制結束回應階段
   */
  forceEnd(): void {
    this.endPhase();
    if (this.resolveWait) {
      this.resolveWait({ responded: false });
      this.resolveWait = null;
    }
  }

  /**
   * 檢查是否在回應階段
   */
  isActive(): boolean {
    return this.state.isActive;
  }

  /**
   * 取得連鎖管理器（進階用途）
   */
  getChainManager(): ResponseChainManager {
    return this.chainManager;
  }

  // ==================== 私有方法 ====================

  private createInitialState(): ResponsePhaseState {
    return {
      isActive: false,
      triggerType: null,
      triggerCardId: null,
      triggerCardName: null,
      triggerPlayerId: null,
      currentPriority: null,
      availableResponses: [],
      timeRemaining: null,
      startTime: null,
      consecutivePasses: 0,
      waitingForAI: false,
    };
  }

  private emit(event: ResponsePhaseEvent): void {
    for (const listener of this.listeners) {
      try {
        listener(event);
      } catch (e) {
        log.error('Listener error:', e);
      }
    }
  }

  private getOpponentId(state: GameState, playerId: string): string {
    return playerId === state.player1.id ? state.player2.id : state.player1.id;
  }

  private getCurrentTimeout(): number {
    if (this.state.consecutivePasses >= this.config.passThreshold) {
      return this.config.reducedTimeout;
    }
    return this.config.defaultTimeout;
  }

  private startTimer(): void {
    this.stopTimer();
    this.timerHandle = setTimeout(() => {
      this.handleTimeout();
    }, this.getCurrentTimeout());
  }

  private stopTimer(): void {
    if (this.timerHandle) {
      clearTimeout(this.timerHandle);
      this.timerHandle = null;
    }
  }

  private handleTimeout(): void {
    const playerId = this.state.currentPriority;
    if (!playerId) return;

    this.emit({ type: 'timeout', playerId });
    this.handlePass(playerId);
  }

  private handlePass(playerId: string): ResponseResult {
    this.stopTimer();

    this.state.consecutivePasses++;
    this.emit({ type: 'pass_received', playerId });

    const shouldResolve = this.chainManager.pass(playerId);

    if (shouldResolve) {
      // 雙方都 pass，應該結算
      this.state.isActive = false;
      this.emit({ type: 'phase_end' });
      if (this.resolveWait) {
        this.resolveWait({ responded: false });
        this.resolveWait = null;
      }
      return { responded: false };
    }

    // 只有一方 pass，需要切換優先權給對方
    // 但這裡沒有 gameState，無法呼叫 switchPriority
    // 所以在 startResponsePhase 中處理這個邏輯

    return { responded: false };
  }

  private async switchPriority(gameState: GameState, newPriorityPlayer: string): Promise<void> {
    this.state.currentPriority = newPriorityPlayer;
    this.state.timeRemaining = this.getCurrentTimeout();
    this.state.startTime = Date.now();
    this.state.waitingForAI = newPriorityPlayer === this.aiPlayerId;

    // 同步 ChainManager 的優先權
    this.chainManager.setCurrentPriority(newPriorityPlayer);

    // 更新可用回應
    const triggerType = this.state.triggerType || 'spell_cast';
    this.state.availableResponses = this.chainManager.getAvailableResponses(
      gameState,
      newPriorityPlayer,
      triggerType
    );

    this.emit({ type: 'priority_change', playerId: newPriorityPlayer });

    if (this.config.enableTimer) {
      this.startTimer();
    }

    // 如果是 AI，自動處理
    if (newPriorityPlayer === this.aiPlayerId) {
      await this.handleAIResponse(gameState, this.state.availableResponses);
    }
  }

  private async handleAIResponse(
    gameState: GameState,
    availableResponses: ResponseOption[]
  ): Promise<void> {
    // AI 思考延遲
    const thinkingDelay = randomBetween(this.config.aiMinDelay, this.config.aiMaxDelay);
    await delay(thinkingDelay);

    // 簡單的 AI 決策：隨機決定是否回應
    // TODO: 實作更智慧的 AI 決策
    const shouldRespond = availableResponses.length > 0 && Math.random() > 0.6;

    if (!this.aiPlayerId) {
      log.warn('AI 玩家 ID 未設定');
      return;
    }

    if (shouldRespond) {
      // 隨機選一個回應
      const response = availableResponses[Math.floor(Math.random() * availableResponses.length)];
      await this.respond(gameState, this.aiPlayerId, response);
    } else {
      this.pass(this.aiPlayerId);
    }
  }

  private endPhase(): void {
    this.stopTimer();
    this.state = this.createInitialState();
    this.chainManager.clearChain();
    this.emit({ type: 'phase_end' });
  }
}

/**
 * 建立回應階段控制器
 */
export function createResponsePhaseController(
  cardDefinitions?: Map<string, CardDefinition>,
  config?: Partial<ResponsePhaseConfig>
): ResponsePhaseController {
  return new ResponsePhaseController(cardDefinitions, config);
}
